import { camelize } from 'camelize-snakeize-ts'
import type { ActionContext, ActionTree } from 'vuex'
import type { State } from '@/store/state'
import dashboardApi from '@/helpers/dashboardApi'
import payoutsApi from '@/helpers/payoutsApi'
import { ReleaseStatus } from '@/constants'
import { getCookie } from '@/helpers/getCookie'
import paymentsApi from '@/helpers/paymentsApi'
import { isAxiosError } from 'axios'

export default <ActionTree <State, State>> {
  async getJwtUser({ commit }: ActionContext <State, State>) {
    const { data } = await dashboardApi.get('me')
    commit('setUser', data)
  },

  async getUserProfile({ commit }: ActionContext <State, State>) {
    const { data } = await dashboardApi.get('me/profile')
    commit('setUserProfile', data)
  },

  async getTransactionDetails(_, id: number): Promise<Transaction> {
    const { data } = await payoutsApi.get(`transactions/${id}`)
    return data
  },

  async getUserActiveReleases({ commit }: ActionContext <State, State>) {
    const { data } = await dashboardApi.get('me/releases/music', {
      params: {
        filters: {
          'status.id': [
            ReleaseStatus.active,
            ReleaseStatus.sentToStores,
            ReleaseStatus.approved,
          ],
        },
      },
    })
    commit('setHasActiveReleases', Boolean(data.length))
  },

  async getUserBalance(): Promise<Balance> {
    const { data } = await dashboardApi.get('me/account_balances')
    return data
  },

  async getUserSubscription({ commit }) {
    try {
      const { data } = await paymentsApi.get('/me/subscription', {
        params: {
          expand: true,
        },
      })

      commit('setUserSubscription', camelize(data))
      commit('setSubscriptionError', false)
    } catch (error: unknown) {
      // If user has no subscription, the API returns a 404
      if (isAxiosError(error) && error.response?.status === 404) {
        commit('setSubscriptionError', false)
      } else {
        commit('setSubscriptionError', true)
      }
    }
  },

  async getUserTransactions(_, page: number): Promise<{ data: Transaction[], meta: Pagination }> {
    const { data } = await payoutsApi.get(`transactions?perPage=10&page=${page}&paginate=true`)
    return data
  },

  async getUserPayoutAccounts({ commit }: ActionContext <State, State>) {
    const { data } = await payoutsApi.get('payout-accounts')
    commit('setPayoutAccounts', data)
  },

  async getPayoneerRegistrationLink(): Promise<string> {
    const { data: { url } } = await payoutsApi.post('payoneer/registration-link', {
      redirectLink: window.location.href,
    })
    return url
  },

  async getTipaltiWidgetLink(): Promise<string> {
    const { data: { widgetUrl } } = await payoutsApi.get('tipalti-widget', {
      params: {
        locale: getCookie('DittoMusic[lang]') || 'en',
      },
    })

    return widgetUrl
  },

  async getUserVerification({ commit }: ActionContext <State, State>) {
    const { data } = await dashboardApi.get('me/verification')
    commit('setUserStatus', data)
  },

  async postPayoutRequest(_, { payoutAmount, payoutAccountId, requestedCurrency, requestedAmount }): Promise<Transaction> {
    const { data } = await payoutsApi.post('transactions', {
      payoutAccount: payoutAccountId,
      amount: payoutAmount,
      requestedCurrency,
      requestedAmount,
    })
    return data
  },

  resendUserVerificationEmail(): Promise<void> {
    return dashboardApi.post('me/resend_activation_email')
  },

  resendPayoutVerificationEmail(_, payoutMethodId: number): Promise<void> {
    return payoutsApi.post(`/payout-accounts/${payoutMethodId}/resend-verification`)
  },

  async getUserCurrencyConversionRate({ state, commit }) {
    const { data: currencyConversionRates } = await dashboardApi.get('public/currency-rates');
    const conversionRateObj = currencyConversionRates.find(
      (rate: { from: string, rate: number, to: string }) => rate.to === state.userProfile.currency,
    );
    const currencyConversionRate = conversionRateObj ? conversionRateObj.rate : 1;
    commit('setUserCurrencyConversionRate', currencyConversionRate);
  },
}
